/**
 * Build the `flight_data` payload sent to `/defera-capital/create-subscription`
 * (and the equivalent Defera Zero endpoint) from the in-flight Review-page state.
 *
 * Why this is its own module: the previous inline IIFE in Review.jsx silently
 * stripped `appliedCredits` and `pricing_snapshot` before posting, which meant
 * Defera Capital bookings lost their airline-credit audit trail. PY2RE8 was the
 * canary — customer paid $1,538.84 on a $1,739 fare with a $500 credit, but the
 * booking row's `flight_data.credits_total` was null. FlightDetailsModal's
 * `creditsTotal` lookup (`parseFloat(flightData.credits_total || 0)`) returned 0,
 * so the green "Credits Applied −$X" line never rendered.
 *
 * The fix mirrors the shape that:
 *   - Slice Pay already forwards via `credits_applied` (initiateSlicePayFlow), and
 *   - miles_installments writes to `abandoned_trips.flight_data` when persisting
 *     a `ready_for_push` cart (see `handleConfirmMilesBooking` in Review.jsx).
 *
 * Keeping the builder here means the credits round-trip is enforced by tests, not
 * hoping a future reviewer notices the spread-and-pick pattern.
 */
import { buildBookingDiscountFields } from '@/utils/discountCodes';
import { normalizeCompanionReference } from '@/utils/companionBookingReference';
import { checkoutMilesFields } from '../../supabase/functions/_shared/miles-redemption.js';

export function buildFlightDataForApi({
  selectedFlight,
  promoApplied,
  appliedCredits = [],
  creditsAppliedTotal = 0,
  combinedBasePrice = 0,
  pricing = null,
  seatData = null,
  discount = null,
  fasterFlightOptIn = null,
  fasterFlightAirlinePref = null,
  travelPurpose = null,
  travelingWithOthers = null,
  companionBooking = null,
  companionBookingReference = '',
  milesRedeemed = 0,
  pricedTravelerCount = 0,
}) {
  const base = {
    ...selectedFlight,
    slices: selectedFlight?.slices || [],
    id: selectedFlight?.id,
    total_amount: selectedFlight?.total_amount,
  };

  // How many travelers the fare was priced for (the search, not the entered
  // travelers). The checkout functions refuse a booking whose travelers don't
  // match it (supabase/functions/_shared/traveler-count-gate.js), and the
  // booking row keeps it so a mismatch can always be audited. C4J47L charged
  // one seat for 3 travelers before this existed.
  delete base._priced_traveler_count;
  if (Number.isInteger(pricedTravelerCount) && pricedTravelerCount > 0) {
    base._priced_traveler_count = pricedTravelerCount;
  }

  // Seat-map picks (seat_selections_detail / seat_assignments_by_index from
  // buildSeatDataForFlightData) — additive fields so booked seats reach the
  // booking row and ultimately the dashboard. Cleared first: a resumed cart's
  // offer can still carry seats the customer no longer has selected, and the
  // server prices whatever seats arrive.
  delete base.seat_selections_detail;
  delete base.seat_assignments_by_index;
  if (seatData && typeof seatData === 'object') {
    Object.assign(base, seatData);
  }

  if (promoApplied) {
    base.promo_applied = true;
  }

  // Discount code (separate, additive effect — NOT the promo fee waiver above).
  // The Defera Capital / Zero rails materialize their bookings inside an edge
  // function from exactly this payload, so a code that isn't carried here would
  // price correctly at checkout and then vanish from the booking row, leaving
  // the creator's redemption stuck at "applied" forever. Same class of bug as
  // the dropped credits this module was written to prevent.
  Object.assign(base, buildBookingDiscountFields(discount));

  // Defera Miles spent on this trip (supabase/functions/_shared/miles-redemption.js).
  // The checkout function prices the plan from these and deducts the Miles
  // before charging; the booking row keeps them so a cancellation can return
  // them. Cleared first so a stale claim on the offer object can never ride
  // along when the customer chose none.
  delete base.miles_redeemed;
  delete base.miles_discount_cents;
  delete base.miles_cents_per_mile;
  Object.assign(base, checkoutMilesFields(milesRedeemed));

  // Airline credits: only the ones applied now (a resumed cart's offer can
  // carry old ones, and the server takes them off the fare).
  delete base.credits;
  delete base.credits_total;
  if (Array.isArray(appliedCredits) && appliedCredits.length > 0) {
    base.credits = appliedCredits;
    base.credits_total = creditsAppliedTotal;
    base.pricing_snapshot = {
      original_base_fare: combinedBasePrice,
      credits_total: creditsAppliedTotal,
      base_fare_after_credits: pricing?.baseFareAfterCredits ?? null,
      service_fee: pricing?.serviceFee ?? null,
      hold_fee: pricing?.holdFee ?? null,
      seat_total: pricing?.seatTotal ?? null,
      grand_total: pricing?.grandTotalIncludingSeats ?? null,
    };
  }

  /** Free Flight Upgrade — the customer's standing answer to "may we move you
   *  to a faster routing at no extra cost?" (checkout, required). Persisted on
   *  the booking because it is a STANDING permission the ops team acts on later,
   *  possibly weeks after purchase: without it on the row, the only record would
   *  be a checkout screen nobody can re-read. `null` is written as an explicit
   *  false rather than left undefined — a missing field must never be mistaken
   *  for consent by a future reader.
   *
   *  Airline scope is only meaningful when opted in; it is omitted otherwise so
   *  the row can't imply a preference the customer never expressed. */
  base.faster_flight_opt_in = fasterFlightOptIn === true;
  if (fasterFlightOptIn === true && fasterFlightAirlinePref != null) {
    base.faster_flight_airline_pref = fasterFlightAirlinePref === 'same' ? 'same' : 'any';
  }
  base.faster_flight_answered_at = new Date().toISOString();

  /* Travel-purpose questions (2026-09-30). All required at checkout, so an
   * unanswered value here means the row predates the questions — never that
   * the customer declined to say.
   *
   * `companion_booking` is only written when they said they ARE travelling
   * with others, for the same reason the airline scope above is conditional:
   * a stored answer to a question nobody was asked reads as a real answer to
   * whoever finds it later. */
  if (travelPurpose != null) base.travel_purpose = travelPurpose;
  if (travelingWithOthers != null) base.traveling_with_others = travelingWithOthers === true;
  if (travelingWithOthers === true && companionBooking != null) {
    base.companion_booking = companionBooking === 'defera' ? 'defera' : 'elsewhere';
  }
  /* The other trip's reference (optional, 2026-10-08): only under "Another
   * Defera booking", and only when something was typed. Cleared first so a
   * resumed cart's offer can't carry an old one. Inserting the booking with
   * it links the two trips for the ops team when both are on this account
   * (migrations 172/173); someone else's trip waits for staff to confirm. */
  delete base.companion_booking_reference;
  const companionRef = normalizeCompanionReference(companionBookingReference);
  if (travelingWithOthers === true && companionBooking === 'defera' && companionRef) {
    base.companion_booking_reference = companionRef;
  }
  if (travelPurpose != null || travelingWithOthers != null) {
    base.travel_questions_answered_at = new Date().toISOString();
  }

  return base;
}
